import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { incomeAPI, userAPI } from '../services/api';
import { Income, CreateIncomeRequest, IncomeType, FinancialSummary } from '../types';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../hooks/useCurrency';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  CurrencyDollarIcon,
  BanknotesIcon,
  BuildingLibraryIcon,
  CalendarDaysIcon,
  ArrowTrendingUpIcon,
  WalletIcon,
} from '@heroicons/react/24/outline';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

const IncomePage: React.FC = () => {
  const { currentUser, isAdmin } = useAuth();
  const { formatAmount } = useCurrency();
  const [myIncomes, setMyIncomes] = useState<Income[]>([]);
  const [familyIncomes, setFamilyIncomes] = useState<Income[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [financialSummary, setFinancialSummary] = useState<FinancialSummary | null>(null);
  const [familyFinancialSummary, setFamilyFinancialSummary] = useState<FinancialSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingIncome, setEditingIncome] = useState<Income | null>(null);

  // Form state
  const [formData, setFormData] = useState<CreateIncomeRequest>({
    amount: 0,
    income_type: 'cash',
    description: '',
    date: new Date().toISOString().split('T')[0],
  });

  const loadData = useCallback(async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      setError(null);

      // Load user's own incomes and financial summary
      const [userIncomes, userSummary] = await Promise.all([
        incomeAPI.getByUser(currentUser.id),
        incomeAPI.getFinancialSummary(currentUser.id)
      ]);

      setMyIncomes(userIncomes);
      setFinancialSummary(userSummary);

      // If admin, also load family data
      if (isAdmin) {
        const [allIncomes, allUsers, familySummary] = await Promise.all([
          incomeAPI.getAll(),
          userAPI.getAll(),
          incomeAPI.getFinancialSummary() // Family summary (no user_id)
        ]);

        // Filter out current user's incomes from family incomes
        const otherUsersIncomes = allIncomes.filter(income => income.user_id !== currentUser.id);
        setFamilyIncomes(otherUsersIncomes);
        setUsers(allUsers);
        setFamilyFinancialSummary(familySummary);
      }
    } catch (err) {
      setError('Failed to load income data');
      console.error('Income loading error:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser, isAdmin]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;

    try {
      console.log('Submitting income data:', formData);
      console.log('JSON stringified:', JSON.stringify(formData));

      if (editingIncome) {
        await incomeAPI.update(editingIncome.id, formData);
      } else {
        await incomeAPI.create(formData);
      }
      
      setShowAddForm(false);
      setEditingIncome(null);
      setFormData({
        amount: 0,
        income_type: 'cash',
        description: '',
        date: new Date().toISOString().split('T')[0],
      });
      loadData();
    } catch (err) {
      setError('Failed to save income');
      console.error('Income save error:', err);
    }
  };

  const handleEdit = (income: Income) => {
    setEditingIncome(income);
    setFormData({
      amount: income.amount,
      income_type: income.income_type,
      description: income.description,
      date: income.date.split('T')[0],
    });
    setShowAddForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this income?')) return;

    try {
      await incomeAPI.delete(id);
      loadData();
    } catch (err) {
      setError('Failed to delete income');
      console.error('Income delete error:', err);
    }
  };

  const getUserName = (userId: string) => {
    const user = users.find(u => u.id === userId);
    return user ? user.name : 'Unknown User';
  };

  const renderFinancialSummary = (summary: FinancialSummary, title: string) => (
    <motion.div
      variants={fadeInUp}
      className="backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-2xl p-6 mb-6 border border-gray-200/50 dark:border-gray-700/50 shadow-lg"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
          <WalletIcon className="h-5 w-5 text-white" />
        </div>
        <h3 className="text-lg font-bold text-gray-900 dark:text-white">{title}</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Total Summary */}
        <motion.div
          whileHover={{ scale: 1.02 }}
          className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 p-5 text-white shadow-lg shadow-blue-500/30"
        >
          <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <h4 className="text-sm font-medium text-blue-100 mb-3">Total Balance</h4>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-blue-100">Income</span>
              <span className="text-sm font-semibold">{formatAmount(summary.total_income)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-blue-100">Expenses</span>
              <span className="text-sm font-semibold">{formatAmount(summary.total_expenses)}</span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t border-white/20">
              <span className="text-sm font-medium">Balance</span>
              <span className="text-lg font-bold">{formatAmount(summary.remaining_balance)}</span>
            </div>
          </div>
        </motion.div>

        {/* Bank Summary */}
        <motion.div
          whileHover={{ scale: 1.02 }}
          className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 p-5 text-white shadow-lg shadow-emerald-500/30"
        >
          <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="flex items-center gap-2 mb-3">
            <BuildingLibraryIcon className="h-4 w-4 text-emerald-100" />
            <h4 className="text-sm font-medium text-emerald-100">Bank Account</h4>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-emerald-100">Income</span>
              <span className="text-sm font-semibold">{formatAmount(summary.bank_income)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-emerald-100">Expenses</span>
              <span className="text-sm font-semibold">{formatAmount(summary.bank_expenses)}</span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t border-white/20">
              <span className="text-sm font-medium">Balance</span>
              <span className="text-lg font-bold">{formatAmount(summary.bank_balance)}</span>
            </div>
          </div>
        </motion.div>

        {/* Cash Summary */}
        <motion.div
          whileHover={{ scale: 1.02 }}
          className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 p-5 text-white shadow-lg shadow-amber-500/30"
        >
          <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="flex items-center gap-2 mb-3">
            <BanknotesIcon className="h-4 w-4 text-amber-100" />
            <h4 className="text-sm font-medium text-amber-100">Cash</h4>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-amber-100">Income</span>
              <span className="text-sm font-semibold">{formatAmount(summary.cash_income)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-amber-100">Expenses</span>
              <span className="text-sm font-semibold">{formatAmount(summary.cash_expenses)}</span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t border-white/20">
              <span className="text-sm font-medium">Balance</span>
              <span className="text-lg font-bold">{formatAmount(summary.cash_balance)}</span>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );

  const renderIncomeList = (incomes: Income[], title: string, showUserName: boolean = false) => (
    <motion.div
      variants={fadeInUp}
      className="backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-2xl mb-6 border border-gray-200/50 dark:border-gray-700/50 shadow-lg overflow-hidden"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-6 border-b border-gray-200/50 dark:border-gray-700/50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
            <ArrowTrendingUpIcon className="h-5 w-5 text-white" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">{title}</h3>
        </div>
        {!showUserName && (
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowAddForm(true)}
            className="mt-4 sm:mt-0 flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold rounded-xl shadow-lg shadow-emerald-500/30 hover:shadow-xl transition-all"
          >
            <PlusIcon className="h-5 w-5" />
            Add Income
          </motion.button>
        )}
      </div>

      {incomes.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center">
            <CurrencyDollarIcon className="h-8 w-8 text-gray-400 dark:text-gray-500" />
          </div>
          <p className="text-gray-500 dark:text-gray-400 font-medium">No income records found</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Add your first income to get started!</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50/50 dark:bg-gray-700/50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Description</th>
                {showUserName && (
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">User</th>
                )}
                {!showUserName && (
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
              {incomes.map((income, index) => (
                <motion.tr
                  key={income.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="hover:bg-gray-50/50 dark:hover:bg-gray-700/30 transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                      <CalendarDaysIcon className="h-4 w-4" />
                      {format(new Date(income.date), 'MMM dd, yyyy')}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                      +{formatAmount(income.amount)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                      income.income_type === 'bank'
                        ? 'bg-gradient-to-r from-blue-100 to-indigo-100 dark:from-blue-900/50 dark:to-indigo-900/50 text-blue-700 dark:text-blue-300'
                        : 'bg-gradient-to-r from-amber-100 to-orange-100 dark:from-amber-900/50 dark:to-orange-900/50 text-amber-700 dark:text-amber-300'
                    }`}>
                      {income.income_type === 'bank' ? <BuildingLibraryIcon className="h-3.5 w-3.5" /> : <BanknotesIcon className="h-3.5 w-3.5" />}
                      {income.income_type === 'bank' ? 'Bank' : 'Cash'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300 max-w-xs truncate">
                    {income.description || <span className="text-gray-400 dark:text-gray-500 italic">No description</span>}
                  </td>
                  {showUserName && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      {getUserName(income.user_id)}
                    </td>
                  )}
                  {!showUserName && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleEdit(income)}
                          className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-800/50 transition-colors"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleDelete(income.id)}
                          className="p-2 rounded-lg bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-800/50 transition-colors"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </motion.button>
                      </div>
                    </td>
                  )}
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </motion.div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full"
        />
      </div>
    );
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="space-y-6"
    >
      {/* Header */}
      <motion.div
        variants={fadeInUp}
        className="backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-lg"
      >
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
            <CurrencyDollarIcon className="h-7 w-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Income Management</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Track your income and monitor your financial balance
            </p>
          </div>
        </div>
      </motion.div>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="backdrop-blur-xl bg-red-50/80 dark:bg-red-900/20 border border-red-200/50 dark:border-red-700/50 rounded-2xl p-4"
        >
          <span className="text-red-700 dark:text-red-300">{error}</span>
        </motion.div>
      )}

      {/* My Income Section */}
      {financialSummary && renderFinancialSummary(financialSummary, "My Financial Summary")}
      {renderIncomeList(myIncomes, "My Income")}

      {/* Family Income Section (Admin Only) */}
      {isAdmin && (
        <>
          {familyFinancialSummary && renderFinancialSummary(familyFinancialSummary, "Family Financial Summary")}
          {renderIncomeList(familyIncomes, "Family Income", true)}
        </>
      )}

      {/* Add/Edit Income Modal */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowAddForm(false);
              setEditingIncome(null);
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md shadow-2xl border border-gray-200/50 dark:border-gray-700/50"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                  {editingIncome ? <PencilSquareIcon className="h-6 w-6 text-white" /> : <PlusIcon className="h-6 w-6 text-white" />}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    {editingIncome ? 'Edit Income' : 'Add New Income'}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {editingIncome ? 'Update income details' : 'Record a new income'}
                  </p>
                </div>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Amount *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                    className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Income Type *</label>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { value: 'cash', label: 'Cash', icon: BanknotesIcon },
                      { value: 'bank', label: 'Bank', icon: BuildingLibraryIcon },
                    ].map((type) => (
                      <motion.button
                        key={type.value}
                        type="button"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setFormData({ ...formData, income_type: type.value as IncomeType })}
                        className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium transition-all ${
                          formData.income_type === type.value
                            ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg shadow-emerald-500/30'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                        }`}
                      >
                        <type.icon className="h-5 w-5" />
                        {type.label}
                      </motion.button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                    placeholder="What is this income for?"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Date *</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <CalendarDaysIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      className="w-full pl-12 pr-4 py-3.5 rounded-xl border-2 border-gray-200 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 text-gray-900 dark:text-white focus:border-emerald-500 dark:focus:border-emerald-400 focus:ring-4 focus:ring-emerald-500/20 transition-all duration-300 cursor-pointer"
                      required
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <motion.button
                    type="button"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setShowAddForm(false);
                      setEditingIncome(null);
                      setFormData({
                        amount: 0,
                        income_type: 'cash',
                        description: '',
                        date: new Date().toISOString().split('T')[0],
                      });
                    }}
                    className="flex-1 px-4 py-3 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-all"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-medium shadow-lg shadow-emerald-500/30 hover:shadow-xl transition-all"
                  >
                    {editingIncome ? 'Update' : 'Add'} Income
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default IncomePage;
